import moment from 'moment';
import 'moment-duration-format';
import 'moment-timezone/builds/moment-timezone-with-data';

/**
 * Gets a timestamp and returns it in 'Do MMMM YYYY' format in a certain time zone.
 *
 * @param {string} timestamp - The timestamp to convert.
 * @param {string} [timezone='Europe/Madrid'] - The time zone to calculate the time.
 * @returns {string} The timestamp in 'Do MMMM YYYY' format.
 */
export const monthDayYear = (timestamp, timezone = 'Europe/Madrid') => {
  const timestampUTC = moment.utc(timestamp);
  return timestampUTC.tz(timezone).format('Do MMMM YYYY');
};

/**
 * Gets a timestamp and returns it in 'Do MMMM YYYY at hh:mm a' format in a certain time zone.
 *
 * @param {string} timestamp - The timestamp to convert.
 * @param {string} [timezone='Europe/Madrid'] - The time zone to calculate the time.
 * @returns {string} The timestamp in 'Do MMMM YYYY at hh:mm a' format.
 */
export const monthDayYearAtTime = (timestamp, timezone = 'Europe/Madrid') => {
  const timestampUTC = moment.utc(timestamp);
  return timestampUTC.tz(timezone).format('Do MMMM YYYY [at] hh:mm a');
};

/**
 * Gets how long a certain timestamp occurred in 'x day/hour/minute(s) ago' or 'a few seconds ago'
 * format in a certain time zone.
 *
 * @param {string} timestamp - The timestamp to check.
 * @param {string} [timezone='Europe/Madrid'] - The time zone to calculate the time.
 * @returns {string} How long the timestamp ocurred in 'x day/hour/minute(s) ago' or 'a few
 * seconds ago' format.
 */
export const timeago = (timestamp, timezone = 'Europe/Madrid') => {
  const timestampUTC = moment.utc(timestamp);
  return timestampUTC.tz(timezone).fromNow();
};

/**
 * Gets a timestamp and returns a year in YYYY format in a certain time zone.
 *
 * @param {string} timestamp - The timestamp to get the year.
 * @param {string} [timezone='Europe/Madrid'] - The time zone to calculate the time.
 * @returns {string} The year of the timestamp.
 */
export const year = (timestamp, timezone = 'Europe/Madrid') => {
  const timestampUTC = moment.utc(timestamp);
  return timestampUTC.tz(timezone).format('YYYY');
};

/**
 * Converts time from microseconds to DD:HH:MM:SS format.
 *
 * @param {number} timeUS - The time to convert in microseconds.
 * @returns {string} The time in DD:HH:MM:SS format.
 */
export const usToDDHHMMSS = timeUS => {
  const timeMS = timeUS / 1e3;
  const duration = moment.duration(timeMS);
  let format = 'm:ss';
  if (duration.days() >= 1) {
    format = 'D:HH:mm:ss';
  } else if (duration.hours() >= 1) {
    format = 'H:mm:ss';
  } else if (duration.minutes() < 1) {
    if (duration.seconds() < 10) {
      return `0:0${duration.seconds()}`;
    }
    return `0:${duration.seconds()}`;
  }
  return duration.format(format);
};

/**
 * Calculates difference between to timestamps.
 *
 * @param {string} timeIso1 - String with the subtracting date.
 * @param {string} timeIso2 - String with the minuend date.
 * @returns {string} Time difference.
 */
export const difference = (timeIso1, timeIso2) => {
  const differenceTimestamp = moment.utc(timeIso2).diff(moment.utc(timeIso1));
  const differenceTime = usToDDHHMMSS(differenceTimestamp * 1000);
  return differenceTime;
};
